import { useEffect, useState, useMemo } from 'react';
import { Home, Search, Plus } from 'lucide-react';
import { usePropertyStore } from '@/stores/propertyStore';
import { useAuthStore } from '@/stores/authStore';
import { PropertyCard } from './PropertyCard';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PropertyStatus } from '@/types';

const statuses: { value: PropertyStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'watchlist', label: 'Watchlist' },
  { value: 'in_review', label: 'Prüfung' },
  { value: 'purchased', label: 'Gekauft' },
  { value: 'in_portfolio', label: 'Bestand' },
  { value: 'sold', label: 'Verkauft' },
  { value: 'dismissed', label: 'Verworfen' },
];

export function PropertyList({ onAddProperty }: { onAddProperty: () => void }) {
  const { properties, filters, isLoading, fetchProperties, setFilters, subscribeToChanges, unsubscribe } = usePropertyStore();
  const { user, isLoading: isAuthLoading } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [hasFetched, setHasFetched] = useState(false);

  // Lade Daten nur wenn User authentifiziert ist
  useEffect(() => {
    // Warte auf Auth-Initialisierung
    if (isAuthLoading) return;

    // Kein User = keine Daten (sollte durch ProtectedRoute nicht passieren)
    if (!user) return;

    // Verhindere doppeltes Laden
    if (hasFetched) return;

    setHasFetched(true);
    fetchProperties();
    subscribeToChanges();

    return () => {
      unsubscribe();
    };
  }, [user, isAuthLoading, hasFetched, fetchProperties, subscribeToChanges, unsubscribe]);

  // Reset hasFetched wenn User sich ändert (z.B. Logout/Login)
  useEffect(() => {
    if (!user) {
      setHasFetched(false);
    }
  }, [user]);

  const counts = properties.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Filter by status and search
  const filtered = useMemo(() => {
    let result = filters.status === 'all' ? properties : properties.filter(p => p.status === filters.status);

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(p =>
        p.name.toLowerCase().includes(search) ||
        p.address_street?.toLowerCase().includes(search) ||
        p.address_city?.toLowerCase().includes(search) ||
        p.notes?.toLowerCase().includes(search)
      );
    }

    return result;
  }, [properties, filters.status, searchTerm]);

  // Zeige Skeleton wenn Auth noch lädt oder Daten noch nicht geladen
  const showSkeleton = isAuthLoading || (isLoading && properties.length === 0) || (!hasFetched && user);

  return (
    <div className="space-y-6">
      {/* Search & Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
          <Input
            type="text"
            placeholder="Immobilien durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-subtle hover:text-foreground"
            >
              ✕
            </button>
          )}
        </div>
        <Button onClick={onAddProperty} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Objekt hinzufügen
        </Button>
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
        {statuses.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilters({ status: s.value })}
            className={cn(
              'flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-150',
              filters.status === s.value
                ? 'bg-foreground text-background'
                : 'bg-background-subtle text-foreground-muted hover:bg-background-elevated hover:text-foreground'
            )}
          >
            {s.label}
            <span className={cn(
              'text-xs px-1.5 py-0.5 rounded-full',
              filters.status === s.value ? 'bg-background/20 text-background' : 'bg-background-elevated text-foreground-subtle'
            )}>
              {counts[s.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {showSkeleton && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1,2,3,4,5,6].map((i) => (
            <div key={i} className="card p-0 overflow-hidden">
              <Skeleton className="aspect-[16/10]" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!showSkeleton && filtered.length === 0 && (
        <EmptyState
          icon={Home}
          title={searchTerm ? 'Keine Ergebnisse' : 'Keine Immobilien'}
          description={searchTerm ? `Keine Immobilien gefunden für "${searchTerm}"` : (filters.status === 'all' ? 'Füge deine erste Immobilie hinzu.' : 'Keine Immobilien mit diesem Status.')}
          action={filters.status === 'all' && !searchTerm ? { label: 'Immobilie hinzufügen', onClick: onAddProperty } : undefined}
        />
      )}

      {!showSkeleton && filtered.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
      )}
    </div>
  );
}
